import { useCallback } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import {
	useAnalyticsEvents,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import { useSubProduct } from '@atlassian/jira-subproduct/src/index.tsx';
import {
	isFeatureVisited,
	getFeatureVisitedCount,
	increaseFeatureVisitedCount,
} from '../services/caching/index.tsx';
import { useEligibilityChecked } from '../services/feature-exposed/index.tsx';
import { useUsersFromCollaborationGraph } from '../services/get-collaborators/index.tsx';
import { getConfluenceUsers } from '../services/permission-check-bulk/index.tsx';
import { hasConfluenceAccess } from '../services/permission-check/index.tsx';
import { useConfluenceRequestCapabilities } from '../services/request-capabilities/index.tsx';
import { usePostThirdPartyNudge } from '../services/send-third-party-nudge/index.tsx';
import { useSiteEligibilityCheck } from '../services/site-eligibility/index.tsx';

const MAX_FEATURE_VISIT_COUNT = 50;
export const useCrossJoinThirdPartyNudgeExperiment = () => {
	const cloudId = useCloudId();
	const fireEligibilityChecked = useEligibilityChecked();
	const getRequestCapabilities = useConfluenceRequestCapabilities(cloudId);
	const isSite7DaysOlder = useSiteEligibilityCheck(cloudId);
	const getCollaborators = useUsersFromCollaborationGraph(cloudId);
	const sendThirdPartyNudge = usePostThirdPartyNudge();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const userId = useAccountId() || '';
	const licensedProducts = useLicensedProducts();
	const subProduct = useSubProduct();

	return useCallback(() => {
		const runExperiment = async () => {
			try {
				if (!licensedProducts.confluence) {
					fireEligibilityChecked('NO_CONFLUENCE_LICENSE');
					return;
				}

				if (isFeatureVisited()) {
					return;
				}

				if (await hasConfluenceAccess(cloudId)) {
					fireEligibilityChecked('ALREADY_HAVE_ACCESS');
					return;
				}

				const requestCapability = await getRequestCapabilities();
				if (!(requestCapability === 'DIRECT_ACCESS' || requestCapability === 'REQUEST_ACCESS')) {
					fireEligibilityChecked('NOT_ELIGIBLE_FOR_REQUEST_ACCESS');
					return;
				}

				if (!(await isSite7DaysOlder())) {
					fireEligibilityChecked('NEW_SITE_LESS_THAN_7_DAYS_OLD');
					return;
				}

				const collaborators = await getCollaborators();
				const confluenceUsers = await getConfluenceUsers(collaborators, cloudId);
				if (confluenceUsers.length === 0) {
					fireEligibilityChecked('COLLABORATORS_HAVE_NO_ACCESS');
					return;
				}

				const featureVisitedCount = await getFeatureVisitedCount(userId);
				if (featureVisitedCount >= MAX_FEATURE_VISIT_COUNT) {
					return;
				}

				// fire manual exposure as it passed all common eligibility checks
				fireEligibilityChecked('ELIGIBLE');

				// send nudge to Pi Gateway service
				sendThirdPartyNudge(featureVisitedCount, subProduct, confluenceUsers, requestCapability);

				increaseFeatureVisitedCount(userId, featureVisitedCount + 1);

				fireOperationalAnalytics(
					createAnalyticsEvent({}),
					'slo.crossJoinThirdPartyNudgesConfluence succeeded',
				);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'useCrossJoinThirdPartyNudgesExperiment',
						packageName: 'jiraCrossJoinThirdPartyNudges',
					},
					attributes: {
						cloudId,
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
			}
		};

		runExperiment();
	}, [
		cloudId,
		createAnalyticsEvent,
		fireEligibilityChecked,
		getCollaborators,
		sendThirdPartyNudge,
		getRequestCapabilities,
		isSite7DaysOlder,
		userId,
		licensedProducts.confluence,
		subProduct,
	]);
};
